import { musicBus } from './index';

/**
 * The score.
 *
 * Streamed, not decoded. Every other sound here is either synthesised or a
 * short file small enough to hold as a decoded buffer, but the music is
 * eighteen minutes long: `decodeAudioData` would turn 8MB of MP3 into roughly
 * 400MB of raw PCM sitting in memory, which is the sort of thing that gets a
 * browser tab killed. An <audio> element streams it instead, decoding a few
 * seconds at a time, and starts playing long before the download finishes.
 *
 * It is routed through the Web Audio graph anyway, so the music slider, the
 * master slider and the mute switch all govern it exactly like everything
 * else. The file lives in /public rather than the asset pipeline because it
 * should be served as a plain streamable file, not bundled.
 *
 * Nothing here assumes the file exists: a missing track is silence.
 */
const TRACK = '/music/castle-ledger.mp3';

let element: HTMLAudioElement | null = null;
let source: MediaElementAudioSourceNode | null = null;
let wanted = false;
/**
 * Whether the score is allowed at all, regardless of settings.
 *
 * False while signed out: the login page is a door, not a place, and the
 * autoplay-unlock handler used to start the music on the first click a
 * visitor made anywhere - including the click that typed their password.
 */
let allowed = true;

export function setMusicAllowed(next: boolean): void {
  if (allowed === next) return;
  allowed = next;
  if (!allowed) {
    const was = wanted;
    stopMusic();
    // stopMusic clears the intent; keep it so signing in resumes the score.
    wanted = was;
  } else if (wanted) {
    startMusic();
  }
}

/**
 * Begin, or resume, the score. Safe to call repeatedly: the element is made
 * once and reused, because a MediaElementSourceNode can only ever be created
 * once per element.
 */
export function startMusic(): void {
  wanted = true;
  if (!allowed) return;
  const bus = musicBus();
  if (!bus) return;

  if (!element) {
    element = new Audio(TRACK);
    element.loop = true;
    element.preload = 'none';
    element.crossOrigin = 'anonymous';
    // The graph owns the level; the element itself always runs wide open.
    element.volume = 1;
    element.addEventListener('error', () => {
      // No track shipped, or it failed to load. Silence, never an exception.
      wanted = false;
    });
    /*
     * `loop` alone is not enough for a streamed track.
     *
     * A long MP3 served without a usable duration reports `Infinity`, and an
     * element that does not know where the end is has nothing to loop back
     * from: it simply reaches the last frame and stops. Restarting on `ended`
     * covers that, and costs nothing when `loop` did its job, because then
     * `ended` never fires in the first place.
     */
    element.addEventListener('ended', () => {
      if (!wanted || !element) return;
      element.currentTime = 0;
      void element.play().catch(() => {});
    });
  }

  if (!source) {
    try {
      source = bus.ctx.createMediaElementSource(element);
      source.connect(bus.out);
    } catch {
      return;
    }
  }

  void element.play().catch(() => {
    /* Autoplay refused; the next user gesture will try again. */
  });
}

export function stopMusic(): void {
  wanted = false;
  element?.pause();
}

/** Whether the player wants music, regardless of whether it is audible now. */
export function musicWanted(): boolean {
  return wanted;
}
